import { useRef, useState, type KeyboardEvent, type PointerEvent as RPointerEvent } from 'react';
import { BUILDER_ELEMENTS, STARTERS, fillHydrogens, removeHydrogens, type BondOrder, type Sketch } from '../lib/moleculeBuilder';

type Lang = 'en' | 'ro' | 'tr';

const TEXT = {
  en: {
    element: 'Element', starters: 'Start from', fillH: 'Add hydrogens', removeH: 'Remove hydrogens', undo: 'Undo', clear: 'Clear',
    del: 'Delete selected', area: 'Molecule drawing area',
    help: 'Click an empty spot to add an atom — if an atom is selected, the new one bonds to it. Click two atoms to bond them. Click a bond to switch single → double → triple. Drag atoms to move them. Choosing an element while an atom is selected changes that atom.',
    names: { methane: 'methane', ammonia: 'ammonia', ethene: 'ethene', ethyne: 'ethyne', ethanol: 'ethanol', aceticAcid: 'acetic acid', benzene: 'benzene', cyclohexane: 'cyclohexane' },
  },
  ro: {
    element: 'Element', starters: 'Pornește de la', fillH: 'Completează cu hidrogen', removeH: 'Elimină hidrogenii', undo: 'Anulează', clear: 'Șterge tot',
    del: 'Șterge selecția', area: 'Zona de desenare a moleculei',
    help: 'Apasă pe un loc liber pentru a adăuga un atom — dacă un atom este selectat, cel nou se leagă de el. Apasă pe doi atomi pentru a-i lega. Apasă pe o legătură pentru simplă → dublă → triplă. Trage atomii pentru a-i muta. Dacă alegi un element când un atom este selectat, atomul se schimbă.',
    names: { methane: 'metan', ammonia: 'amoniac', ethene: 'etenă', ethyne: 'etină', ethanol: 'etanol', aceticAcid: 'acid acetic', benzene: 'benzen', cyclohexane: 'ciclohexan' },
  },
  tr: {
    element: 'Element', starters: 'Hazır başlangıç', fillH: 'Hidrojenle tamamla', removeH: 'Hidrojenleri kaldır', undo: 'Geri al', clear: 'Temizle',
    del: 'Seçileni sil', area: 'Molekül çizim alanı',
    help: 'Boş bir yere tıklayınca atom eklenir — bir atom seçiliyse yeni atom ona bağlanır. İki atoma sırayla tıklayınca aralarına bağ kurulur. Bir bağa tıklayınca tekli → ikili → üçlü olur. Atomları sürükleyerek taşıyabilirsin. Bir atom seçiliyken element seçersen o atom değişir.',
    names: { methane: 'metan', ammonia: 'amonyak', ethene: 'eten', ethyne: 'etin', ethanol: 'etanol', aceticAcid: 'asetik asit', benzene: 'benzen', cyclohexane: 'siklohekzan' },
  },
} as const;

const COLORS: Record<string, { fill: string; text: string; stroke: string }> = {
  C: { fill: '#3a3f47', text: '#ffffff', stroke: '#22262c' },
  H: { fill: '#ffffff', text: '#18202c', stroke: '#9aa3b0' },
  O: { fill: '#d63a3a', text: '#ffffff', stroke: '#a32626' },
  N: { fill: '#3563d6', text: '#ffffff', stroke: '#2447a3' },
  S: { fill: '#e2b41b', text: '#18202c', stroke: '#a88410' },
  P: { fill: '#e57f17', text: '#18202c', stroke: '#a85c0f' },
  F: { fill: '#58c45c', text: '#18202c', stroke: '#3a8c3d' },
  Cl: { fill: '#2fa84a', text: '#ffffff', stroke: '#1f7333' },
  Br: { fill: '#9b3424', text: '#ffffff', stroke: '#6e2418' },
  I: { fill: '#7a3ab5', text: '#ffffff', stroke: '#55287f' },
  B: { fill: '#f2a6a6', text: '#18202c', stroke: '#c07575' },
};
const W = 600;
const H = 360;
const R = 17;

export function MoleculeBuilder({ sketch, onChange, lang }: { sketch: Sketch; onChange: (s: Sketch) => void; lang: Lang }) {
  const L = TEXT[lang] ?? TEXT.en;
  const svgRef = useRef<SVGSVGElement>(null);
  const history = useRef<Sketch[]>([]);
  const drag = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const [element, setElement] = useState<string>('C');
  const [selected, setSelected] = useState<number | null>(null);

  const commit = (next: Sketch) => {
    history.current = [...history.current.slice(-49), sketch];
    onChange(next);
  };
  const undo = () => {
    const prev = history.current.pop();
    if (prev) { onChange(prev); setSelected(null); }
  };

  const toSvg = (clientX: number, clientY: number) => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: Math.max(R, Math.min(W - R, p.x)), y: Math.max(R, Math.min(H - R, p.y)) };
  };

  const nextId = () => Math.max(0, ...sketch.atoms.map((a) => a.id)) + 1;
  const findBond = (a: number, b: number) => sketch.bonds.findIndex((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a));

  const addAtomAt = (x: number, y: number) => {
    const id = nextId();
    const atoms = [...sketch.atoms, { id, el: element, x, y }];
    const bonds = selected !== null ? [...sketch.bonds, { a: selected, b: id, order: 1 as BondOrder }] : sketch.bonds;
    commit({ atoms, bonds });
    setSelected(id);
  };

  const clickAtom = (id: number) => {
    if (selected === null) { setSelected(id); return; }
    if (selected === id) { setSelected(null); return; }
    const k = findBond(selected, id);
    const bonds = [...sketch.bonds];
    if (k >= 0) bonds[k] = { ...bonds[k], order: (bonds[k].order === 3 ? 1 : bonds[k].order + 1) as BondOrder };
    else bonds.push({ a: selected, b: id, order: 1 });
    commit({ atoms: sketch.atoms, bonds });
    setSelected(null);
  };

  const cycleBond = (k: number) => {
    const bonds = [...sketch.bonds];
    bonds[k] = { ...bonds[k], order: (bonds[k].order === 3 ? 1 : bonds[k].order + 1) as BondOrder };
    commit({ atoms: sketch.atoms, bonds });
  };

  const deleteSelected = () => {
    if (selected === null) return;
    commit({ atoms: sketch.atoms.filter((a) => a.id !== selected), bonds: sketch.bonds.filter((b) => b.a !== selected && b.b !== selected) });
    setSelected(null);
  };

  const pickElement = (el: string) => {
    setElement(el);
    if (selected !== null) commit({ atoms: sketch.atoms.map((a) => (a.id === selected ? { ...a, el } : a)), bonds: sketch.bonds });
  };

  const onAtomDown = (e: RPointerEvent<SVGGElement>, id: number) => {
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const p = toSvg(e.clientX, e.clientY);
    drag.current = { id, x: p.x, y: p.y, moved: false };
  };
  const onMove = (e: RPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    const p = toSvg(e.clientX, e.clientY);
    if (!d.moved && Math.hypot(p.x - d.x, p.y - d.y) < 5) return;
    if (!d.moved) { d.moved = true; history.current = [...history.current.slice(-49), sketch]; }
    onChange({ atoms: sketch.atoms.map((a) => (a.id === d.id ? { ...a, x: p.x, y: p.y } : a)), bonds: sketch.bonds });
  };
  const onAtomUp = (e: RPointerEvent<SVGGElement>, id: number) => {
    e.stopPropagation();
    const d = drag.current;
    drag.current = null;
    if (d && !d.moved) clickAtom(id);
  };
  const onBackground = (e: RPointerEvent<SVGRectElement>) => {
    if (drag.current) return;
    const p = toSvg(e.clientX, e.clientY);
    addAtomAt(p.x, p.y);
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelected(); }
    if (e.key === 'Escape') setSelected(null);
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); }
  };

  const byId = new Map(sketch.atoms.map((a) => [a.id, a]));

  return (
    <section className="panel" aria-label={L.area}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <span className="h-small" style={{ margin: 0 }}>{L.element}:</span>
        {BUILDER_ELEMENTS.map((el) => (
          <button key={el} type="button" className={el === element ? 'chip-btn on' : 'chip-btn'} aria-pressed={el === element} onClick={() => pickElement(el)} style={{ minWidth: 44 }}>
            {el}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <span className="h-small" style={{ margin: 0 }}>{L.starters}:</span>
        {(Object.keys(STARTERS) as Array<keyof typeof STARTERS>).map((k) => (
          <button key={k} type="button" className="chip-btn" onClick={() => { commit(STARTERS[k]()); setSelected(null); }}>
            {L.names[k as keyof typeof L.names] ?? k}
          </button>
        ))}
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="application"
        aria-label={L.area}
        tabIndex={0}
        onPointerMove={onMove}
        onKeyDown={onKey}
        style={{ width: '100%', height: 'auto', display: 'block', background: '#fbfaf6', border: '1px solid #ddd8cb', borderRadius: 12, touchAction: 'none', userSelect: 'none' }}
      >
        <defs>
          <pattern id="mb-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M20 0 L0 0 0 20" fill="none" stroke="rgba(24,32,44,.06)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect x="0" y="0" width={W} height={H} fill="url(#mb-grid)" onPointerUp={onBackground} />
        {sketch.bonds.map((b, k) => {
          const A = byId.get(b.a);
          const B = byId.get(b.b);
          if (!A || !B) return null;
          const dx = B.x - A.x;
          const dy = B.y - A.y;
          const l = Math.hypot(dx, dy) || 1;
          const nx = -dy / l;
          const ny = dx / l;
          const offs = b.order === 1 ? [0] : b.order === 2 ? [-4, 4] : [-7, 0, 7];
          return (
            <g key={`b${k}`} onPointerUp={(e) => { e.stopPropagation(); if (!drag.current) cycleBond(k); }} style={{ cursor: 'pointer' }}>
              <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="transparent" strokeWidth="18" />
              {offs.map((o) => (
                <line key={o} x1={A.x + nx * o} y1={A.y + ny * o} x2={B.x + nx * o} y2={B.y + ny * o} stroke="#4b5260" strokeWidth="3" strokeLinecap="round" />
              ))}
            </g>
          );
        })}
        {sketch.atoms.map((a) => {
          const c = COLORS[a.el] ?? COLORS.C;
          const r = a.el === 'H' ? R - 4 : R;
          return (
            <g key={a.id} onPointerDown={(e) => onAtomDown(e, a.id)} onPointerUp={(e) => onAtomUp(e, a.id)} style={{ cursor: 'grab' }}>
              {selected === a.id && <circle cx={a.x} cy={a.y} r={r + 6} fill="none" stroke="#0d7068" strokeWidth="3" />}
              <circle cx={a.x} cy={a.y} r={r} fill={c.fill} stroke={c.stroke} strokeWidth="2" />
              <text x={a.x} y={a.y} textAnchor="middle" dominantBaseline="central" fontSize={a.el.length > 1 ? 12 : 14} fontWeight="700" fill={c.text} style={{ pointerEvents: 'none' }}>
                {a.el}
              </text>
            </g>
          );
        })}
      </svg>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        <button type="button" className="btn" onClick={() => commit(fillHydrogens(sketch))}>{L.fillH}</button>
        <button type="button" className="btn" onClick={() => { commit(removeHydrogens(sketch)); setSelected(null); }}>{L.removeH}</button>
        <button type="button" className="btn" onClick={deleteSelected} disabled={selected === null}>{L.del}</button>
        <button type="button" className="btn" onClick={undo}>{L.undo}</button>
        <button type="button" className="btn btn-ghost" onClick={() => { commit({ atoms: [], bonds: [] }); setSelected(null); }}>{L.clear}</button>
      </div>
      <p className="hint">{L.help}</p>
    </section>
  );
}
